import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import {
  ApiService,
  WishlistItem,
  EventModel,
  EventTicketType,
} from '@hypenation/tickets';
import { FilesService } from '@hypenation/shared';
import { HttpResponse } from '@angular/common/http';
import { Observable, catchError, map, of, switchMap, throwError } from 'rxjs';

@Component({
  selector: 'hypenation-wishlist',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    NzCardModule,
    NzSpinModule,
    NzEmptyModule,
    NzButtonModule,
    NzIconModule,
    NzGridModule,
    NzPopconfirmModule,
  ],
  templateUrl: './wishlist.component.html',
  styleUrl: './wishlist.component.css',
})
export class WishlistComponent implements OnInit {
  loading = false;
  wishlistItems: WishlistItem[] = [];
  imageUrls = new Map<string, string>();
  addingToCart = new Set<string>();

  private readonly apiService = inject(ApiService);
  private readonly filesService = inject(FilesService);
  private readonly notification = inject(NzNotificationService);
  private readonly router = inject(Router);

  private getWishlistEventUid(item: WishlistItem): string | null {
    const row = item as WishlistItem & { eventUid?: string };
    return row.eventUid ?? null;
  }

  private resolveEventUidFromTicketType(
    item: WishlistItem
  ): Observable<{ eventUid: string; source: string }> {
    return this.apiService.getEventTicketTypeByUid(item.ticketTypeUid).pipe(
      map((ticketTypeRes: HttpResponse<EventTicketType>) => {
        const eventUid = ticketTypeRes.body?.eventUid;
        if (!eventUid) {
          throw new Error('Ticket type did not return eventUid');
        }

        return { eventUid, source: 'ticketType.eventUid' };
      }),
      catchError((err) => throwError(() => err))
    );
  }

  private resolveEventUidForWishlistItem(
    item: WishlistItem
  ): Observable<{ eventUid: string; source: string }> {
    const eventUidFromRow = this.getWishlistEventUid(item);
    const productEqualsTicketType = item.productUid === item.ticketTypeUid;

    if (eventUidFromRow) {
      return of({ eventUid: eventUidFromRow, source: 'eventUid' });
    }

    if (productEqualsTicketType) {
      return this.resolveEventUidFromTicketType(item);
    }

    return this.apiService.getEventByUid(item.productUid).pipe(
      map((eventRes: HttpResponse<EventModel>) => {
        const eventUid = eventRes.body?.uid ?? item.productUid;
        return { eventUid, source: 'productUid' };
      }),
      catchError(() => this.resolveEventUidFromTicketType(item))
    );
  }

  ngOnInit(): void {
    this.loadWishlist();
  }

  loadWishlist(): void {
    this.loading = true;
    this.apiService.getMyWishlist().subscribe({
      next: (res) => {
        this.wishlistItems = res.body ?? [];

        this.loadImages();
        this.loading = false;
      },
      error: () => (this.loading = false),
    });
  }

  private loadImages(): void {
    this.wishlistItems.forEach((item) => {
      if (item.productImageFileName && !this.imageUrls.has(item.uid)) {
        this.filesService
          .getFileDownloadUrl(item.productImageFileName)
          .subscribe({
            next: (url) => this.imageUrls.set(item.uid, url),
            error: () => this.imageUrls.set(item.uid, 'assets/placeholder.jpg'),
          });
      }
    });
  }

  private navigateToEventDetail(eventUid: string, ticketTypeUid: string): void {
    this.apiService.getEventByUid(eventUid).subscribe({
      next: (eventRes: HttpResponse<EventModel>) => {
        const event = eventRes.body;
        if (!event?.slug || !event?.eventType?.uid) {
          this.notification.error(
            'Unable to open event page',
            'We could not open the event details page. Please try from the events list.'
          );
          return;
        }

        this.router.navigate(
          ['/tickets', event.slug, event.uid, event.eventType.uid],
          {
            queryParams: { ticketTypeUid },
          }
        );
      },
      error: () => {
        this.notification.error(
          'Unable to open event page',
          'We could not open the event details page. Please try from the events list.'
        );
      },
    });
  }

  buyTicket(item: WishlistItem): void {
    this.addingToCart.add(item.uid);

    this.resolveEventUidForWishlistItem(item)
      .pipe(
        switchMap(({ eventUid }) => {
          this.navigateToEventDetail(eventUid, item.ticketTypeUid);

          return of(null);
        })
      )
      .subscribe({
        next: () => {
          this.addingToCart.delete(item.uid);
        },
        error: () => {
          this.addingToCart.delete(item.uid);

          this.notification.error(
            'Could not open event details',
            'Please try again or open the event from the Events page.'
          );
        },
      });
  }

  isAddingToCart(itemUid: string): boolean {
    return this.addingToCart.has(itemUid);
  }

  removeItem(item: WishlistItem): void {
    this.apiService.removeFromWishlist(item.uid).subscribe({
      next: () => {
        this.wishlistItems = this.wishlistItems.filter(
          (i) => i.uid !== item.uid
        );
        this.notification.success(
          'Removed',
          `${item.productName} removed from wishlist`
        );
      },
      error: () => {
        this.notification.error('Error', 'Failed to remove item from wishlist');
      },
    });
  }

  getImageUrl(item: WishlistItem): string {
    return this.imageUrls.get(item.uid) || 'assets/placeholder.jpg';
  }
}
